import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../api/client";

interface UserRow {
  id: number;
  username: string | null;
  email: string | null;
  display_name: string | null;
  is_admin_cached: boolean;
  has_password: boolean;
  last_seen_at: string | null;
  ai_credits_month: number;
}

export function UserManagement({ currentUserId }: { currentUserId: number }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => api.adminListUsers(),
  });
  // The server returns ids as numbers OR strings (bigint). Normalize.
  const users: UserRow[] = (q.data?.users ?? []).map((u) => ({
    id: Number(u.id),
    username: u.username,
    email: u.email,
    display_name: u.display_name,
    is_admin_cached: u.is_admin_cached,
    has_password: u.has_password,
    last_seen_at: u.last_seen_at,
    ai_credits_month: Number(u.ai_credits_month ?? 0),
  }));
  const adminCount = users.filter((u) => u.is_admin_cached).length;

  const [addOpen, setAddOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState<UserRow | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "users"] });

  const toggleAdmin = useMutation({
    mutationFn: (u: UserRow) => api.adminPatchUser(u.id, { isAdmin: !u.is_admin_cached }),
    onSuccess: refresh,
  });

  const del = useMutation({
    mutationFn: (id: number) => api.adminDeleteUser(id),
    onSuccess: refresh,
  });

  return (
    <section className="rounded border border-zinc-800 bg-zinc-900 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-zinc-100">Users</h2>
        <button
          onClick={() => setAddOpen(true)}
          className="rounded bg-emerald-700 px-3 py-1 text-sm text-white hover:bg-emerald-600"
        >
          Add user
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-zinc-500">
          <tr>
            <th className="py-1">Username</th>
            <th>Display name</th>
            <th>Admin</th>
            <th>Last seen</th>
            <th>AI credits (month)</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const isSelf = u.id === currentUserId;
            const wouldOrphan = u.is_admin_cached && adminCount <= 1;
            return (
              <tr key={u.id} className="border-t border-zinc-800">
                <td className="py-1 font-mono text-zinc-200">
                  {u.username ?? <span className="text-zinc-500">(oidc)</span>}
                </td>
                <td className="text-zinc-300">{u.display_name ?? ""}</td>
                <td>
                  <button
                    onClick={() => toggleAdmin.mutate(u)}
                    disabled={wouldOrphan}
                    title={wouldOrphan ? "Can't demote the last admin" : undefined}
                    className={
                      "rounded px-2 py-0.5 text-xs " +
                      (u.is_admin_cached
                        ? "bg-emerald-700 text-white"
                        : "bg-zinc-700 text-zinc-300") +
                      (wouldOrphan ? " cursor-not-allowed opacity-60" : " hover:opacity-80")
                    }
                  >
                    {u.is_admin_cached ? "admin" : "user"}
                  </button>
                </td>
                <td className="text-xs text-zinc-500">
                  {u.last_seen_at ? new Date(u.last_seen_at).toLocaleString() : "—"}
                </td>
                <td className="text-zinc-300">{u.ai_credits_month}</td>
                <td className="text-right">
                  {u.has_password && (
                    <button
                      onClick={() => setPwTarget(u)}
                      className="mr-2 rounded bg-zinc-800 px-2 py-0.5 text-xs text-zinc-200 hover:bg-zinc-700"
                    >
                      Set password
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete user ${u.username ?? u.id}?`)) del.mutate(u.id);
                    }}
                    disabled={isSelf || wouldOrphan}
                    title={
                      isSelf
                        ? "Can't delete yourself"
                        : wouldOrphan
                          ? "Can't delete the last admin"
                          : undefined
                    }
                    className="rounded bg-rose-800/60 px-2 py-0.5 text-xs text-rose-100 hover:bg-rose-800 disabled:opacity-50"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {addOpen && (
        <AddUserModal
          onClose={() => setAddOpen(false)}
          onCreated={() => {
            setAddOpen(false);
            refresh();
          }}
        />
      )}
      {pwTarget && (
        <SetPasswordModal
          user={pwTarget}
          onClose={() => setPwTarget(null)}
          onSaved={() => {
            setPwTarget(null);
            refresh();
          }}
        />
      )}
    </section>
  );
}

function AddUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.adminCreateUser({
        username,
        password,
        displayName: displayName || undefined,
        isAdmin,
      }),
    onSuccess: () => onCreated(),
    onError: (e: unknown) => {
      if (e instanceof ApiError) {
        const body = e.body as { error?: string } | null;
        if (body?.error === "username_taken") setErr("That username is already taken.");
        else setErr("Create failed.");
      } else {
        setErr("Create failed.");
      }
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="w-full max-w-sm rounded border border-zinc-800 bg-zinc-900 p-6"
      >
        <h3 className="mb-3 text-base font-semibold text-zinc-100">Add user</h3>
        <label className="mb-2 block text-xs text-zinc-400">
          Username
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            className="mt-1 block w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-zinc-100"
          />
        </label>
        <label className="mb-2 block text-xs text-zinc-400">
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-zinc-100"
          />
        </label>
        <label className="mb-2 block text-xs text-zinc-400">
          Display name (optional)
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="mt-1 block w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-zinc-100"
          />
        </label>
        <label className="mb-3 flex items-center gap-2 text-xs text-zinc-300">
          <input
            type="checkbox"
            checked={isAdmin}
            onChange={(e) => setIsAdmin(e.target.checked)}
          />
          Make admin
        </label>
        {err && (
          <div className="mb-2 rounded bg-rose-900/40 px-2 py-1 text-xs text-rose-200">{err}</div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-zinc-800 px-3 py-1 text-sm text-zinc-200 hover:bg-zinc-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending || username.length === 0 || password.length < 8}
            className="rounded bg-emerald-700 px-3 py-1 text-sm text-white hover:bg-emerald-600 disabled:opacity-50"
          >
            {create.isPending ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}

function SetPasswordModal({
  user,
  onClose,
  onSaved,
}: {
  user: UserRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api.adminPatchUser(user.id, { password }),
    onSuccess: () => onSaved(),
    onError: () => setErr("Save failed."),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="w-full max-w-sm rounded border border-zinc-800 bg-zinc-900 p-6"
      >
        <h3 className="mb-3 text-base font-semibold text-zinc-100">
          Set password for {user.username}
        </h3>
        <label className="mb-3 block text-xs text-zinc-400">
          New password (min 8 chars)
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            className="mt-1 block w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-zinc-100"
          />
        </label>
        {err && (
          <div className="mb-2 rounded bg-rose-900/40 px-2 py-1 text-xs text-rose-200">{err}</div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-zinc-800 px-3 py-1 text-sm text-zinc-200 hover:bg-zinc-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={save.isPending || password.length < 8}
            className="rounded bg-emerald-700 px-3 py-1 text-sm text-white hover:bg-emerald-600 disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
